import React from "react";
import { StatusBadge } from "../../u1/StatusBadge";
import { MemberPill } from "../../u1/MemberPill";

/* Sidebar widget: what the three profile states mean. */
export const RESULT_STEPS = [
  {
    status: "listed",
    copy: "Created from public trade-licence and registry data. Every business starts here, before it is claimed.",
  },
  {
    status: "claimed",
    copy: "The owner has proven they control the business and now manages the profile.",
  },
  {
    status: "verified",
    copy: "A Claimed profile whose identity and details TREVEX has independently checked. The strongest trust signal in the directory.",
  },
];

/* My Companies lists only claimed businesses, so the Listed step has nothing
   to describe there. */
export const CLAIMED_STEPS = RESULT_STEPS.filter((s) => s.status !== "listed");

/* A step may bring its own pill instead of a profile status — Purchased
   describes order states, which are not statuses — and its own rail colour,
   since those states are not all "further along" the way the badges are. */
const TONES = {
  success: { rail: "bg-[var(--success)]", node: "border-[var(--success)]" },
  warning: { rail: "bg-[var(--warning)]", node: "border-[var(--warning)]" },
  muted: { rail: "bg-[var(--line-2)]", node: "border-[var(--muted-2)]" },
};

export const StateLegend = ({
  eyebrow = "Reading the results",
  steps = RESULT_STEPS,
  title = "What the badges mean",
  intro = "Every profile has one status. It shows how far a business has come on TREVEX.",
  /* The membership note below the divider. Suppressed on screens where
     membership is beside the point — Purchased describes orders. */
  showMembershipNote = true,
}) => (
  <section
    className="rounded-xl border border-[var(--line)] bg-white p-5"
    aria-labelledby="u1-legend-heading"
  >
    <p className="text-[11px] font-bold tracking-[.08em] text-[var(--muted)] font-sans uppercase m-0">
      {eyebrow}
    </p>
    <h2
      id="u1-legend-heading"
      className="text-[17px] font-bold text-[var(--ink)] font-sans mt-1 mb-1.5"
    >
      {title}
    </h2>
    <p className="text-[12px] leading-[1.55] text-[var(--muted)] font-sans m-0">
      {intro}
    </p>

    {/* Vertical stepper */}
    <ol className="relative mt-4 m-0 p-0 list-none">
      {steps.map((step, i) => {
        const tone = TONES[step.tone] || TONES.success;
        return (
          <li key={step.status || step.id} className="relative pl-6 pb-4 last:pb-0">
            {/* Rail: a line from this node to the next, plus the node itself. */}
            {i < steps.length - 1 && (
              <span
                aria-hidden="true"
                className={`absolute left-[5px] top-[14px] bottom-0 w-px ${tone.rail}`}
              />
            )}
            <span
              aria-hidden="true"
              className={`absolute left-0 top-[7px] w-[11px] h-[11px] rounded-full border-[1.5px] bg-white ${tone.node}`}
            />
            {step.badge || <StatusBadge status={step.status} />}
            <p className="text-[12px] leading-[1.55] text-[var(--muted)] font-sans mt-1.5 m-0">
              {step.copy}
            </p>
          </li>
        );
      })}
    </ol>

    {showMembershipNote && (
      <>
        <hr className="my-4 border-0 border-t border-dashed border-[var(--line-2)]" />

        <p className="text-[11px] font-bold tracking-[.08em] text-[var(--muted)] font-sans uppercase m-0 mb-2.5">
          Not a status
        </p>
        <div className="flex gap-3 items-start">
          <MemberPill className="shrink-0 mt-0.5" />
          <p className="text-[12px] leading-[1.55] text-[var(--muted)] font-sans m-0">
            A paid Membership. It unlocks the Member rate, Payment Endorsements, and the Partner
            Program. It sits beside the business name, not in the status.
          </p>
        </div>
      </>
    )}
  </section>
);

export default StateLegend;
